import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "vehicle-lookup-card" }
const _hoisted_2 = ["value", "placeholder"]
const _hoisted_3 = {
  key: 0,
  class: "list-group vehicle-lookup-card__menu"
}
const _hoisted_4 = ["onMousedown"]
const _hoisted_5 = { class: "ms-2 vehicle-lookup-card__detail" }
const _hoisted_6 = {
  key: 0,
  class: "card mt-2 border-success"
}
const _hoisted_7 = { class: "card-body py-2" }
const _hoisted_8 = { class: "mb-1 text-success fw-bold" }
const _hoisted_9 = { class: "mb-0" }
const _hoisted_10 = {
  key: 0,
  class: "mb-0 text-muted small"
}
const _hoisted_11 = {
  key: 1,
  class: "mb-0 text-muted small"
}
const _hoisted_12 = { class: "d-flex gap-2 mt-2" }

import { ref, computed } from 'vue'
import VehicleRepository from '@/repositories/VehicleRepository'
import { Vehicle } from '@/types/Vehicle'
import { StrHelper } from '@/helpers/StrHelper'
import type { VehiclePayload } from '@/types/VehiclePayload'
import VehicleCreateModal from './VehicleCreateModal.vue'

type LookupState = 'idle' | 'found'


export default /*@__PURE__*/_defineComponent({
  __name: 'VehicleLookupCard',
  props: {
    modelValue: {}
  },
  emits: ["update:modelValue", "select"],
  setup(__props: any, { emit: __emit }) {



const emit = __emit

const state = ref<LookupState>('idle')
const foundVehicle = ref<Vehicle | null>(null)
const createModalRef = ref<InstanceType<typeof VehicleCreateModal> | null>(null)
const plateInput = ref('')
const suggestions = ref<Vehicle[]>([])
const activeIndex = ref(-1)
const inputFocused = ref(false)
let debounceTimer: ReturnType<typeof setTimeout> | null = null

// Dropdown is open when: input is focused, ≥3 chars typed, no vehicle selected yet
const dropdownOpen = computed(
  () => inputFocused.value && plateInput.value.length >= 3 && state.value !== 'found'
)

function onInput(event: Event): void {
  const normalized = StrHelper.formatPlate((event.target as HTMLInputElement).value)
  plateInput.value = normalized
  ;(event.target as HTMLInputElement).value = normalized

  state.value = 'idle'
  foundVehicle.value = null
  suggestions.value = []
  activeIndex.value = -1

  if (debounceTimer !== null) clearTimeout(debounceTimer)
  if (normalized.length < 3) return

  debounceTimer = setTimeout(() => fetchSuggestions(normalized), 300)
}

async function fetchSuggestions(term: string): Promise<void> {
  try {
    const { vehicles } = await VehicleRepository.list({ search: term, perPage: 20 })
    if (plateInput.value === term) {
      suggestions.value = vehicles
    }
  } catch {
    suggestions.value = []
  }
}

function onKeyDown(event: KeyboardEvent): void {
  if (!dropdownOpen.value) return
  // Total items: suggestions + create option
  const total = suggestions.value.length + 1

  if (event.code === 'ArrowDown') {
    event.preventDefault()
    activeIndex.value = (activeIndex.value + 1) % total
  } else if (event.code === 'ArrowUp') {
    event.preventDefault()
    activeIndex.value = activeIndex.value <= 0 ? total - 1 : activeIndex.value - 1
  } else if (event.code === 'Enter' && activeIndex.value >= 0) {
    event.preventDefault()
    if (activeIndex.value < suggestions.value.length) {
      selectSuggestion(suggestions.value[activeIndex.value])
    } else {
      openCreateModal()
    }
  } else if (event.code === 'Escape') {
    inputFocused.value = false
    activeIndex.value = -1
  }
}

function onBlur(): void {
  setTimeout(() => {
    inputFocused.value = false
    activeIndex.value = -1
  }, 150)
}

function onFocus(): void {
  inputFocused.value = true
}

function selectSuggestion(vehicle: Vehicle): void {
  plateInput.value = vehicle.plate
  suggestions.value = []
  activeIndex.value = -1
  inputFocused.value = false
  foundVehicle.value = vehicle
  state.value = 'found'
}

function selectFound(): void {
  if (!foundVehicle.value) return
  const payload: VehiclePayload = { vehicleId: foundVehicle.value.id }
  emit('update:modelValue', payload)
  emit('select', payload)
}

function resetPlate(): void {
  foundVehicle.value = null
  plateInput.value = ''
  state.value = 'idle'
  suggestions.value = []
  activeIndex.value = -1
  inputFocused.value = false
  emit('update:modelValue', null)
}

function openCreateModal(): void {
  inputFocused.value = false
  createModalRef.value?.open(plateInput.value)
}

function onVehicleCreated(vehicleId: string): void {
  const payload: VehiclePayload = { vehicleId }
  emit('update:modelValue', payload)
  emit('select', payload)
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: _normalizeClass(["form-group position-relative vehicle-lookup-card__field", { 'vehicle-lookup-card__field--open': dropdownOpen.value }])
    }, [
      _createElementVNode("label", null, _toDisplayString(_ctx.$t('vehicles.fields.plate')), 1),
      _createElementVNode("input", {
        class: "form-control form-control-sm vehicle-lookup-card__input",
        type: "text",
        value: plateInput.value,
        placeholder: _ctx.$t('vehicles.fields.plate'),
        onInput: onInput,
        onFocus: onFocus,
        onKeydown: onKeyDown,
        onBlur: onBlur,
        autocomplete: "none"
      }, null, 40, _hoisted_2),
      (dropdownOpen.value)
        ? (_openBlock(), _createElementBlock("ul", _hoisted_3, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(suggestions.value, (v, idx) => {
              return (_openBlock(), _createElementBlock("li", {
                key: v.id,
                class: _normalizeClass(["list-group-item list-group-item-action small vehicle-lookup-card__item", { active: idx === activeIndex.value }]),
                onMousedown: _withModifiers(($event: any) => (selectSuggestion(v)), ["prevent"])
              }, [
                _createElementVNode("strong", null, _toDisplayString(v.plate), 1),
                _createElementVNode("span", _hoisted_5, _toDisplayString(v.brand) + " " + _toDisplayString(v.model), 1)
              ], 42, _hoisted_4))
            }), 128)),
            _createElementVNode("li", {
              class: _normalizeClass(["list-group-item list-group-item-action small fw-semibold vehicle-lookup-card__item vehicle-lookup-card__item--create", { active: activeIndex.value === suggestions.value.length }]),
              onMousedown: _withModifiers(openCreateModal, ["prevent"])
            }, " + " + _toDisplayString(_ctx.$t('vehicles.lookup.create_with_plate', { plate: plateInput.value })), 35)
          ]))
        : _createCommentVNode("", true)
    ], 2),
    (state.value === 'found' && foundVehicle.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            _createElementVNode("p", _hoisted_8, _toDisplayString(_ctx.$t('vehicles.lookup.found')), 1),
            _createElementVNode("p", _hoisted_9, [
              _createElementVNode("strong", null, _toDisplayString(foundVehicle.value.brand), 1),
              _createTextVNode(" — " + _toDisplayString(foundVehicle.value.model), 1)
            ]),
            (foundVehicle.value.linked_drivers && foundVehicle.value.linked_drivers.length)
              ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(_ctx.$t('vehicles.lookup.linked_to_n_drivers', { n: foundVehicle.value.linked_drivers.length })), 1))
              : _createCommentVNode("", true),
            (foundVehicle.value.currently_driven_by)
              ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(_ctx.$t('vehicles.lookup.currently_driven_by_x', { name: foundVehicle.value.currently_driven_by.name })), 1))
              : _createCommentVNode("", true),
            _createElementVNode("div", _hoisted_12, [
              _createElementVNode("button", {
                type: "button",
                class: "btn btn-sm btn-success",
                onClick: selectFound
              }, _toDisplayString(_ctx.$t('vehicles.lookup.link_this_vehicle')), 1),
              _createElementVNode("button", {
                type: "button",
                class: "btn btn-sm btn-outline-secondary",
                onClick: resetPlate
              }, _toDisplayString(_ctx.$t('vehicles.lookup.different_plate')), 1)
            ])
          ])
        ]))
      : _createCommentVNode("", true),
    _createVNode(VehicleCreateModal, {
      ref_key: "createModalRef",
      ref: createModalRef,
      onCreated: onVehicleCreated
    }, null, 512)
  ]))
}
}

})